import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save, Send, CheckCircle2, XCircle, MessageCircle, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageTitle, Card, Field, TextInput, TextArea, Btn, ListEditor, Badge } from "@/components/admin/ui";
import { Switch } from "@/components/ui/switch";

const AlertsCard = ({ form, set }) => {
  const { data: status } = useQuery({ queryKey: ["alerts-status"], queryFn: async () => (await api.get("/admin/alerts/status")).data });
  const test = useMutation({
    mutationFn: async () => (await api.post("/admin/alerts/test")).data,
    onSuccess: (d) => {
      const sent = d.results.filter((r) => r.status === "sent").length;
      const failed = d.results.filter((r) => r.status !== "sent");
      if (sent) toast.success(`Test alert sent via ${d.results.filter((r) => r.status === "sent").map((r) => r.channel).join(", ")}`);
      failed.forEach((r) => toast.error(`${r.channel} → ${r.to}: ${r.error}`));
    },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const Channel = ({ ok, icon: Icon, label, detail }) => (
    <div className="flex items-center gap-3 rounded-md border border-white/10 px-4 py-3">
      <Icon className={ok ? "h-4 w-4 text-emerald-400" : "h-4 w-4 text-slate-500"} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="truncate text-xs text-slate-500">{detail}</p>
      </div>
      {ok ? <Badge tone="green">Connected</Badge> : <Badge>{label === "WhatsApp" ? "Manual" : "Not configured"}</Badge>}
    </div>
  );
  return (
    <Card title="Lead alerts" className="xl:col-span-2" action={<Btn type="button" variant="ghost" onClick={() => test.mutate()} disabled={test.isPending} data-testid="alerts-test-button">{test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test alert</Btn>}>
      <p className="mb-5 text-sm text-slate-400">Every new test-drive request or enquiry is pushed to your team the moment it arrives. Credentials for WhatsApp (Twilio) and email (Resend) live in the backend environment; recipients are managed here.</p>
      <div className="grid gap-3 sm:grid-cols-2" data-testid="alerts-channel-status">
        <Channel ok={!!status?.whatsapp_configured} icon={MessageCircle} label="WhatsApp" detail={status?.whatsapp_configured ? `Automatic via Twilio from ${status.whatsapp_from}` : "Manual: use the “Forward” button on any lead to send it to the number below. Automatic sending needs Twilio."} />
        <Channel ok={!!status?.email_configured} icon={Mail} label="Email via Resend" detail={status?.email_configured ? `Sending from ${status.email_from}` : "Add RESEND_API_KEY"} />
      </div>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Field label="Sales team WhatsApp number" hint="Used by the Forward button (and Twilio alerts if connected). Country code, digits only e.g. 923009166666">
          <div className="flex items-center gap-3">
            <TextInput value={form.alert_whatsapp_numbers ?? ""} onChange={set("alert_whatsapp_numbers")} data-testid="settings-alert_whatsapp_numbers-input" />
            <Switch checked={form.alerts_whatsapp_enabled ?? true} onCheckedChange={set("alerts_whatsapp_enabled")} data-testid="settings-alerts-whatsapp-switch" />
          </div>
        </Field>
        <Field label="Email recipients" hint="Comma-separated">
          <div className="flex items-center gap-3">
            <TextInput value={form.alert_emails ?? ""} onChange={set("alert_emails")} data-testid="settings-alert_emails-input" />
            <Switch checked={form.alerts_email_enabled ?? true} onCheckedChange={set("alerts_email_enabled")} data-testid="settings-alerts-email-switch" />
          </div>
        </Field>
      </div>
    </Card>
  );
};

export default function SettingsAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data, staleTime: 0 });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);

  if (isLoading || !form) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const setLine = (i) => (e) => setForm((f) => { const hero_lines = [...(f.hero_lines || [])]; hero_lines[i] = e.target.value; return { ...f, hero_lines }; });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, hero_lines: (form.hero_lines || []).filter(Boolean), stats: (form.stats || []).filter((s) => s.label && s.value) };
      await api.put("/admin/settings", body);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const T = (label, key, props = {}) => <Field label={label} hint={props.hint}><TextInput value={form[key] ?? ""} onChange={set(key)} data-testid={`settings-${key}-input`} placeholder={props.placeholder} /></Field>;

  return (
    <form onSubmit={save} data-testid="settings-form">
      <PageTitle eyebrow="Website" title="Site Settings">
        <Btn type="submit" disabled={saving} data-testid="settings-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes</Btn>
      </PageTitle>
      <div className="grid gap-6 xl:grid-cols-2">
        <AlertsCard form={form} set={set} />
        <Card title="Brand & hero">
          <div className="grid gap-5 sm:grid-cols-2">
            {T("Dealership name", "dealership_name")}
            {T("Brand name", "brand_name")}
            <Field label="Tagline" className="sm:col-span-2"><TextInput value={form.tagline} onChange={set("tagline")} data-testid="settings-tagline-input" /></Field>
            <Field label="Hero eyebrow" className="sm:col-span-2"><TextInput value={form.hero_eyebrow} onChange={set("hero_eyebrow")} data-testid="settings-hero_eyebrow-input" /></Field>
            {[0, 1, 2].map((i) => <Field key={i} label={`Headline line ${i + 1}`} hint={i === 1 ? "Shown in italic gold" : undefined}><TextInput value={form.hero_lines?.[i] ?? ""} onChange={setLine(i)} data-testid={`settings-hero-line-${i}-input`} /></Field>)}
            <Field label="Hero subtitle" className="sm:col-span-2"><TextArea rows={3} value={form.hero_sub} onChange={set("hero_sub")} data-testid="settings-hero_sub-input" /></Field>
            <Field label="Announcement pill" hint="Leave empty to hide" className="sm:col-span-2"><TextInput value={form.announcement} onChange={set("announcement")} data-testid="settings-announcement-input" /></Field>
          </div>
        </Card>

        <Card title="About">
          <div className="space-y-5">
            {T("About title", "about_title")}
            <Field label="About text" hint="First sentence is shown as the italic lead"><TextArea rows={8} value={form.about_text} onChange={set("about_text")} data-testid="settings-about_text-input" /></Field>
          </div>
        </Card>

        <Card title="Contact & location">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Address" className="sm:col-span-2"><TextInput value={form.address} onChange={set("address")} data-testid="settings-address-input" /></Field>
            {T("City", "city")}
            {T("Email", "email")}
            {T("Sales phone", "phone")}
            {T("Service / parts phone", "phone_alt")}
            {T("WhatsApp number", "whatsapp", { hint: "Digits only, with country code e.g. 923009166666" })}
            {T("Google Maps link", "map_url")}
            <Field label="Map embed URL" className="sm:col-span-2" hint="Google Maps embed src (…&output=embed)"><TextInput value={form.map_embed} onChange={set("map_embed")} data-testid="settings-map_embed-input" /></Field>
            {T("Showroom hours", "hours_showroom")}
            {T("Workshop hours", "hours_workshop")}
          </div>
        </Card>

        <Card title="Social & stats">
          <div className="grid gap-5 sm:grid-cols-2">
            {T("Facebook URL", "facebook")}
            {T("Instagram URL", "instagram")}
            {T("TikTok URL", "tiktok")}
            {T("YouTube URL", "youtube")}
          </div>
          <div className="mt-8">
            <ListEditor testid="stats-editor" label="Homepage stats (4 recommended)" rows={form.stats || []} onChange={set("stats")} blank={{ label: "", value: "" }} max={4} fields={[{ key: "value", placeholder: "3S" }, { key: "label", placeholder: "Sales · Service · Parts" }]} />
          </div>
        </Card>
      </div>
    </form>
  );
}
